'use client'

import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
// eslint-disable-next-line no-restricted-imports
import cookies from 'js-cookie'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { initReactI18next, useTranslation } from 'react-i18next'
import {
  type Language,
  cookieName,
  defaultNameSpace,
  languageCodes,
  localisationConfig,
} from '../constants'
import { pipePostProcessor } from '../utils'
import { getConfig } from '@/tools/config'
import { setRequestHeaders } from '@/tools/fetcher/client'
import { useErrorHandling } from '@/tools/o11y/client'

let isInitialised = false

const initLocalisation = () => {
  isInitialised = true
  const isBrowser = typeof window !== 'undefined'
  void i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(resourcesToBackend((language: string) => import(`../locales/${language}.json`)))
    .use(pipePostProcessor)
    .init({
      ...localisationConfig,
      lng: undefined,
      detection: { order: ['path', 'cookie'] },
      preload: isBrowser ? [] : languageCodes,
      postProcess: ['pipePostProcessor'],
    })
}

export const useLocalisation = () => {
  const { isBrowser } = getConfig()
  const pathName = usePathname()
  const lastSetLanguageRef = useRef<string | null>(null)
  const isNextInternalRoute = pathName.startsWith('/api')
  const languageInUrl = (!isNextInternalRoute && pathName.split('/')[1]) || 'en'

  if (!isInitialised) {
    initLocalisation()
    if (isBrowser) {
      setRequestHeaders('Glovo-Language-Code', languageInUrl)
      setRequestHeaders('Accept-Language', languageInUrl)
    }
  }

  const { i18n } = useTranslation(defaultNameSpace)
  const { execWithErrorTracking } = useErrorHandling()
  const searchParams = useSearchParams()
  const router = useRouter()
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null)

  const isSupportedLanguage = (language: unknown): language is Language =>
    languageCodes.some((lang) => lang === language)

  const getLanguage = (): Language => {
    const language = i18n.resolvedLanguage
    if (!language) {
      throw new Error('[getLanguage] Language not found')
    }
    return language as Language
  }

  const setLanguage = async (language: string) => {
    const shouldChangeLanguage = language !== selectedLanguage
    if (shouldChangeLanguage) {
      if (isSupportedLanguage(language)) {
        // throw new Error(`[setLanguage] Language ${language} is not supported`)
        cookies.set(cookieName, language)
        setSelectedLanguage(language)
        setRequestHeaders('Glovo-Language-Code', language)
        setRequestHeaders('Accept-Language', language)
        await i18n.changeLanguage(language)
      }
    }
  }

  const loadLanguageAndRefresh = async (language: Language) => {
    const shouldChangeLanguage = language !== selectedLanguage
    if (isSupportedLanguage(language) && shouldChangeLanguage) {
      await setLanguage(language)
      let newUrl = pathName.replace(languageInUrl, language)
      const searchParameters = searchParams.toString()
      if (searchParameters) {
        newUrl += `?${searchParameters}`
      }
      router.push(newUrl)
    }
  }

  const initServerSideLanguage = () => {
    void setLanguage(languageInUrl)
  }

  if (!isBrowser) {
    initServerSideLanguage()
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      const refreshLanguage = async () => {
        if (isSupportedLanguage(languageInUrl) && languageInUrl !== lastSetLanguageRef.current) {
          lastSetLanguageRef.current = languageInUrl
          await setLanguage(languageInUrl)
        }
      }

      void refreshLanguage()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageInUrl])

  return {
    getLanguage,
    loadLanguageAndRefresh,
    setLanguage,
    t: i18n.t,
  }
}

export type UseLocalisationClient = ReturnType<typeof useLocalisation>
